import { useEffect, useState } from "react";
import {
  createStockMovement,
  listInventoryItems,
  listStockMovements,
  listWarehouses,
  type AdminSession,
  type InventoryItemOption,
  type InventoryMovementItem,
  type WarehouseItem,
} from "@/lib/api";

type MovementType = "in" | "out" | "adjustment";

type MovementLine = {
  inventoryItemId: string;
  quantity: number;
};

type MovementHeaderForm = {
  warehouseId: string;
  personName: string;
  movementType: MovementType;
};

const emptyLine: MovementLine = { inventoryItemId: "", quantity: 1 };
const emptyHeader: MovementHeaderForm = { warehouseId: "", personName: "", movementType: "out" };

function formatMovementType(value: string): string {
  if (value === "in") return "Entrada";
  if (value === "out") return "Salida";
  if (value === "adjustment") return "Ajuste";
  return value;
}

export default function MovimientosSection({ session }: { session: AdminSession }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [movements, setMovements] = useState<InventoryMovementItem[]>([]);

  const [header, setHeader] = useState<MovementHeaderForm>(emptyHeader);
  const [lines, setLines] = useState<MovementLine[]>([{ ...emptyLine }]);

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const [itemData, warehouseData, movementData] = await Promise.all([
        listInventoryItems(currentSession),
        listWarehouses(currentSession),
        listStockMovements(currentSession, { limit: 100 }),
      ]);
      setItems(itemData);
      setWarehouses(warehouseData);
      setMovements(movementData);
      setHeader((prev) => ({ ...prev, warehouseId: prev.warehouseId || warehouseData[0]?.id || "" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar movimientos");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  function updateLine(index: number, patch: Partial<MovementLine>): void {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function addLine(): void {
    setLines((prev) => [...prev, { ...emptyLine }]);
  }

  function removeLine(index: number): void {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  async function handleCreateMovement(): Promise<void> {
    if (!header.warehouseId || !header.personName.trim()) {
      setError("Completa bodega y persona responsable.");
      return;
    }
    const validLines = lines.filter((line) => line.inventoryItemId && Number(line.quantity) !== 0);
    if (validLines.length === 0) {
      setError("Agrega al menos un ítem con cantidad para el movimiento.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const referenceId = `MAN-${Date.now()}`;
      for (const line of validLines) {
        await createStockMovement(session, {
          warehouseId: header.warehouseId,
          inventoryItemId: line.inventoryItemId,
          movementType: header.movementType,
          quantity: Math.abs(Number(line.quantity)),
          referenceType: "manual",
          referenceId,
          personName: header.personName.trim(),
        });
      }
      setLines([{ ...emptyLine }]);
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo registrar el movimiento");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {error ? (
        <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="lg:col-span-1">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">Nuevo Movimiento</h2>
          <div className="space-y-2">
            <select value={header.warehouseId} onChange={(e) => setHeader((p) => ({ ...p, warehouseId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Bodega</option>
              {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
            <input value={header.personName} onChange={(e) => setHeader((p) => ({ ...p, personName: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Persona" />
            <select value={header.movementType} onChange={(e) => setHeader((p) => ({ ...p, movementType: e.target.value as MovementType }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="in">Entrada</option>
              <option value="out">Salida</option>
              <option value="adjustment">Ajuste</option>
            </select>
          </div>

          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-gray-500">Detalle</span>
              <button onClick={addLine} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">+ Agregar ítem</button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="rounded-lg border border-gray-100 p-2">
                  <select value={line.inventoryItemId} onChange={(e) => updateLine(index, { inventoryItemId: e.target.value })} className="mb-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                    <option value="">Producto</option>
                    {items.map((item) => <option key={item.id} value={item.id}>{item.sku} · {item.name}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="any"
                      value={line.quantity}
                      onChange={(e) => updateLine(index, { quantity: Number(e.target.value || 0) })}
                      className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                      placeholder="Cantidad"
                    />
                    <button onClick={() => removeLine(index)} disabled={lines.length === 1} className="shrink-0 rounded-lg border border-red-200 px-2 py-1.5 text-xs text-red-700 disabled:opacity-40">✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <button disabled={saving} onClick={() => void handleCreateMovement()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">Registrar movimiento</button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Movimientos recientes</h2>
            <span className="text-xs text-gray-500">{movements.length} registros</span>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Cargando movimientos...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase text-gray-500">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Bodega</th>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Cantidad</th>
                    <th className="px-3 py-2">Persona</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id} className="border-b border-gray-50 text-sm text-gray-700">
                      <td className="px-3 py-2 text-xs">{movement.createdAt ? new Date(movement.createdAt).toLocaleString("es-CL") : "-"}</td>
                      <td className="px-3 py-2">{movement.warehouseCode || "-"}</td>
                      <td className="px-3 py-2">{movement.itemSku} · {movement.itemName}</td>
                      <td className="px-3 py-2">{formatMovementType(movement.movementType)}</td>
                      <td className="px-3 py-2">{movement.quantity}</td>
                      <td className="px-3 py-2">{movement.personName || "-"}</td>
                    </tr>
                  ))}
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-4 text-sm text-gray-500">No hay movimientos registrados.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
